import { Input } from "antd";
import { type FC, useState } from "react";

import { ValidationFormConfig } from "src/app/features/ApplicationConfigurations/components/validations/ValidationFormConfig";
import { ValidationSelectorNumber } from "src/app/features/ApplicationConfigurations/components/validations/ValidationSelectorNumber";
import { ValidationSelectorRequired } from "src/app/features/ApplicationConfigurations/components/validations/ValidationSelectorRequired";
import { ValidationSelectorString } from "src/app/features/ApplicationConfigurations/components/validations/ValidationSelectorString";
import { ValidationTags } from "src/app/features/ApplicationConfigurations/components/validations/ValidationTags";
import type { AppConfigsValidation, ValidationSelectorMapType } from "src/app/features/ApplicationConfigurations/types/AppConfigsTypes";
import { DisplayText } from "../../../../../components/EditableList/components/ListComponents/DisplayText";
import type { AntCustomFormField, ValidationRule, ValidationType } from "../../../../../components/Form/types/types";
import { FormComponent } from "../../../../../components/Form/types/types";

export interface ApplicationConfigurationsValidationsEditProps extends AntCustomFormField<AppConfigsValidation> {}

export const ApplicationConfigurationsValidationsEdit: FC<ApplicationConfigurationsValidationsEditProps> = ({ onChange, value }) => {
    const [selectedValidation, setSelectedValidation] = useState<ValidationType>();

    const validationValue = value?.validations || [];
    const componentType = value?.componentType;

    const onSelected = (selected: ValidationType) => {
        setSelectedValidation(selected);
    };

    const onRemoveTag = (tags: ValidationRule[]) => {
        if (onChange && componentType) {
            onChange({ componentType, validations: tags });
        }
    };

    const onAddTag = (newTag: ValidationRule) => {
        if (validationValue && onChange && componentType) {
            const filterIndex = validationValue.findIndex((tag) => tag.name === newTag.name);
            if (filterIndex === -1) {
                onChange({ componentType, validations: [...validationValue, newTag] });
            }
        }
    };

    // No Validations because it is not currenlty used within a form, cannot be altered by FE user or because a checkbox cannot have any validations.
    const NO_VALIDATIONS = "No validations available for this field component";

    const validationSelectorMap: Record<FormComponent, ValidationSelectorMapType> = {
        [FormComponent.AUTOCOMPLETE]: <ValidationSelectorString onSelect={onSelected} />,
        [FormComponent.TEXT]: <ValidationSelectorString onSelect={onSelected} />,
        [FormComponent.TEXT_AREA]: <ValidationSelectorString onSelect={onSelected} />,
        [FormComponent.TEXT_TEMPLATING]: <ValidationSelectorString onSelect={onSelected} />,
        [FormComponent.INPUT_GENERATOR]: <ValidationSelectorString onSelect={onSelected} />,
        [FormComponent.SITE_ID_GENERATOR]: <ValidationSelectorString onSelect={onSelected} />,
        [FormComponent.SITE_ID_COPY_GENERATOR]: <ValidationSelectorString onSelect={onSelected} />,
        [FormComponent.TERMINAL_ID_GENERATOR]: <ValidationSelectorString onSelect={onSelected} />,
        [FormComponent.TERMINAL_ID_COPY_GENERATOR]: <ValidationSelectorString onSelect={onSelected} />,
        [FormComponent.NUMBER]: <ValidationSelectorNumber onSelect={onSelected} />,
        [FormComponent.CHECK_BOX]: <DisplayText value={NO_VALIDATIONS} />,
        [FormComponent.HIDDEN_INPUT]: <DisplayText value={NO_VALIDATIONS} />,
        [FormComponent.DISPLAY_TEXT]: <DisplayText value={NO_VALIDATIONS} />,
        [FormComponent.HIDDEN_CHECK_BOX]: <DisplayText value={NO_VALIDATIONS} />,
        [FormComponent.APPLICATION_CONFIGURATIONS_VALIDATIONS_EDIT]: <DisplayText value={NO_VALIDATIONS} />,
        [FormComponent.APPLICATION_CONFIGURATIONS_DEFAULT_VALUE_EDIT]: <DisplayText value={NO_VALIDATIONS} />,
        [FormComponent.ACCEPT_TYPE_SELECTOR]: <DisplayText value={NO_VALIDATIONS} />,
        [FormComponent.READ_ONLY_CHECK]: <DisplayText value={NO_VALIDATIONS} />,
        [FormComponent.SELECT_DYNAMIC]: <ValidationSelectorRequired onSelect={onSelected} />,
        [FormComponent.MULTI_SELECT_DYNAMIC]: <ValidationSelectorRequired onSelect={onSelected} />,
        [FormComponent.DYNAMIC_PROPERTIES]: <ValidationSelectorRequired onSelect={onSelected} />,
        [FormComponent.UPLOAD_FILE]: <ValidationSelectorRequired onSelect={onSelected} />,
        [FormComponent.PROFILE_SELECTOR]: <ValidationSelectorRequired onSelect={onSelected} />,
        [FormComponent.TIME_PICKER]: <ValidationSelectorRequired onSelect={onSelected} />,
        [FormComponent.DATE_PICKER]: <ValidationSelectorRequired onSelect={onSelected} />,
        [FormComponent.FILE_CONTENTS]: <ValidationSelectorRequired onSelect={onSelected} />,
        [FormComponent.SITES_TABLE_VIEW]: <ValidationSelectorRequired onSelect={onSelected} />,
        [FormComponent.DATA_GROUP_USERS_TABLE]: <ValidationSelectorRequired onSelect={onSelected} />,
        [FormComponent.DATA_GROUP_IDM_USERS_TABLE]: <ValidationSelectorRequired onSelect={onSelected} />,
        [FormComponent.DYNAMIC_PROPERTY_GROUP_PROPERTIES_TABLE]: <ValidationSelectorRequired onSelect={onSelected} />,
        [FormComponent.CONTRACTS_TERMINALS_TABLE]: <ValidationSelectorRequired onSelect={onSelected} />,
        [FormComponent.CONTRACTS_APP_PROFILES_TABLE]: <ValidationSelectorRequired onSelect={onSelected} />,
        [FormComponent.CONTRACTS_VIRTUAL_APP_PROFILES_TABLE]: <ValidationSelectorRequired onSelect={onSelected} />,
        [FormComponent.VIRTUAL_ACQUIRERS_PHYSICAL_ACQUIRERS_TABLE]: <ValidationSelectorRequired onSelect={onSelected} />,
        [FormComponent.ACQUIRERS_OPERATORS_TABLE]: <ValidationSelectorRequired onSelect={onSelected} />,
        [FormComponent.SITES_CONTRACTS_TABLE]: <ValidationSelectorRequired onSelect={onSelected} />,
        [FormComponent.APPLICATIONS_PROFILE_PROMPT_DATA]: <ValidationSelectorRequired onSelect={onSelected} />,
        [FormComponent.APPLICATIONS_PROFILE_CARD_DEFINITIONS_TABLE]: <ValidationSelectorRequired onSelect={onSelected} />,
        [FormComponent.PARAMETERS_TABLE]: <ValidationSelectorRequired onSelect={onSelected} />,
        [FormComponent.TERMINAL_CONTRACTS_TABLE]: <ValidationSelectorRequired onSelect={onSelected} />,
        [FormComponent.TERMINAL_HOST_TERMINAL]: <ValidationSelectorRequired onSelect={onSelected} />,
        [FormComponent.CARD_DEFINITIONS_TABLE]: <ValidationSelectorRequired onSelect={onSelected} />,
        [FormComponent.CARD_ATTRIBUTES_TABLE]: <ValidationSelectorRequired onSelect={onSelected} />,
        [FormComponent.ACQUIRERS_CONTRACTS_TABLE]: <ValidationSelectorRequired onSelect={onSelected} />,
        [FormComponent.EMV_KERNEL_EMV_CONFIGURATIONS_TABLE]: <ValidationSelectorRequired onSelect={onSelected} />,
        [FormComponent.EMV_KERNEL_CA_PUBLIC_KEYS_TABLE]: <ValidationSelectorRequired onSelect={onSelected} />,
        [FormComponent.EMV_KERNEL_AID_PREFERENCES_TABLE]: <ValidationSelectorRequired onSelect={onSelected} />,
        [FormComponent.EMV_KERNEL_AID_PREFERENCES_CLESS_TABLE]: <ValidationSelectorRequired onSelect={onSelected} />,
        [FormComponent.CAMPAIGN_TERMINALS_SELECTABLE_TABLE]: <ValidationSelectorRequired onSelect={onSelected} />,
        [FormComponent.TERMINAL_TO_CAMPAIGN_TABLE]: <ValidationSelectorRequired onSelect={onSelected} />,
        [FormComponent.VAT_CODE_VAT_VALUES_TABLE]: <ValidationSelectorRequired onSelect={onSelected} />,
        [FormComponent.CARD_COMBINATIONS]: <ValidationSelectorRequired onSelect={onSelected} />,
        [FormComponent.PRODUCT_PROFILE_PRODUCTS]: <ValidationSelectorRequired onSelect={onSelected} />,
        [FormComponent.CARD_TYPES_MAPPING_TABLE]: <ValidationSelectorRequired onSelect={onSelected} />,
        [FormComponent.VIRTUAL_SITES_TABLE]: <ValidationSelectorRequired onSelect={onSelected} />,
        [FormComponent.CAMPAIGN_TERMINALS_VIEW_TABLE]: <ValidationSelectorRequired onSelect={onSelected} />,
        [FormComponent.TERMINALS_TEXTAREA]: <ValidationSelectorRequired onSelect={onSelected} />,
        [FormComponent.SITES_VIRTUAL_SITES_TABLE]: <ValidationSelectorRequired onSelect={onSelected} />,
        [FormComponent.ORG_UNITS_PRODUCTS_PROFILE]: <ValidationSelectorRequired onSelect={onSelected} />,
        "": null,
    };

    const validationSelectorInput = (componentInput: FormComponent) => {
        return validationSelectorMap[componentInput];
    };

    return (
        <>
            <Input.Group compact>
                {componentType && validationSelectorInput(componentType)}
                {selectedValidation && <ValidationFormConfig value={selectedValidation} onChange={onAddTag} />}
            </Input.Group>
            <ValidationTags tags={validationValue || []} onChange={onRemoveTag} showClose />
        </>
    );
};
